// In FilterStatusBar.tsx

import React, { useCallback, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { resetLeadFilter, setLeadFilter } from "../../actions/actions";
import {
  X, Calendar, Tag, Filter, ArrowDownUp, User, Phone,
  Clock, Database, Mail, MessageCircle, BellRing,
  Package, Settings, Sliders, Bookmark
} from 'lucide-react';
import { FilterParams } from './leadsGrid/leads.types';
import styled from 'styled-components';
import { store } from '../../store/store';
import logger from '../../utils/logger';

// First, add the missing FilterStatusBarProps interface definition:
interface FilterStatusBarProps {
    onEditFilter: (section: string) => void;
    columnFilters?: {[colId: string]: any};
    onRemoveColumnFilter?: (colId: string) => void;
  }

// Component implementation
const FilterStatusBar: React.FC<FilterStatusBarProps> = ({
  onEditFilter,
  columnFilters = {},
  onRemoveColumnFilter
}) => {
  // Redux access
  const dispatch = useDispatch();
  const { filterParam } = useSelector((state: any) => state.rootReducers.leads);
  const userPreferences = useSelector((state: any) => state.rootReducers.user?.userPreferences);
  const employees = useSelector((state: any) => 
    state?.rootReducers?.userData?.userDetails?.organizationEmployee || []);
  
  const products = useSelector((state: any) => 
  state?.rootReducers?.products?.products || []
);

logger.log('🛒 FilterStatusBar - Available products from Redux:', products.length);

  // Helper to get team member name
  const getTeamMemberName = useCallback((memberId: string): string => {
    const member = employees.find((emp: any) => emp._id === memberId);
    if (member) {
      return `${member.firstName || ''} ${member.lastName || ''}`.trim();
    }
    return memberId; // Fallback
  }, [employees]);
  
  // Add right after your other data access code
const teamData = useSelector((state: any) => 
    state?.rootReducers?.userData?.userDetails?.organizationTeams || []);
  
  // Team name lookup helper function
  const getTeamName = useCallback((teamId: string): string => {
    // Special case for organization
    if (teamId === 'organization') {
      return 'Organization';
    }
    
    // Find the team by ID
    const team = teamData.find((t: any) => t._id === teamId);
    if (team) {
      return team.name || teamId;
    }
    
    return teamId; // Fallback to ID if not found
  }, [teamData]);

  // Helper to get label name
  const getLabelName = useCallback((labelValue: string): string => {
    if (!userPreferences?.labels) return labelValue;
    
    const label = userPreferences.labels.find((l: any) => l.value === labelValue);
    return label ? label.name : labelValue;
  }, [userPreferences?.labels]);
  
  // Helper to get status name
  const getStatusName = useCallback((statusValue: string): string => {
    if (!userPreferences?.status) return statusValue;
    
    const status = userPreferences.status.find((s: any) => s.value === statusValue);
    return status ? status.name : statusValue;
  }, [userPreferences?.status]);
  
  const systemIntegrations = useSelector((state: any) => 
    state?.rootReducers?.userData?.userDetails?.systemIntegration || []
  );
  

const getProductName = useCallback((productId: string): string => {
 
  
  // Find product in Redux products array
  const product = products.find((p: any) => p._id === productId);
  
  if (product) {
    logger.log('✅ Found product name:', product.name);
    
    return product.name;
  }
  
  logger.log('❌ Product not found in Redux products');
  logger.log('❌ Available product IDs:', products.map((p: any) => `${p.name} (${p._id})`));
  
  
  // Fallback to showing partial ID
  return `Product ...${productId.slice(-8)}`;
}, [products]); // Use products from Redux, not filterParam._productNames

logger.log('🛒 FilterStatusBar render - Products available:', products.length);

  const getSourceName = useCallback((sourceValue: string): string => {
    const integration = systemIntegrations.find(
      (int: any) => int._id === sourceValue
    );

    return integration?.name || sourceValue;
  }, [systemIntegrations]);

  // Helper to get quick filter display name
  const getQuickFilterName = useCallback((quickFilterValue: string): string => {
    const quickFilterLabels: { [key: string]: string } = {
      'recent_assign_leads': 'Recently Assigned',
      'today_task': "Today's Tasks",
      'over_due_task': 'Overdue Tasks',
      'recent_call': 'Recent Calls'
    };
    return quickFilterLabels[quickFilterValue] || quickFilterValue;
  }, []);
  
  // Helper to format operator name
  const formatOperator = useCallback((operator: string): string => {
    const operatorMap: {[key: string]: string} = {
      'eq': '=',
      'ne': '≠',
      'gt': '>',
      'lt': '<',
      'contain': 'contains',
      'doesNotContain': '!contains',
      'gte': '≥',
      'lte': '≤',
      'startsWith': 'starts with',
      'endsWith': 'ends with'
    };
    return operatorMap[operator] || operator;
  }, []);

  // Helper to get column display name from colId
  const getColumnDisplayName = useCallback((colId: string): string => {
    // Format column ID to readable name (e.g., "firstName" -> "First Name")
    return colId
      .replace(/([A-Z])/g, ' $1')
      .replace(/^./, str => str.toUpperCase())
      .trim();
  }, []);
  
  // Date range formatter
  const formatDateRange = useCallback((dateObj: any): string => {
    if (!dateObj) return 'Date Filter';

    try {
      if (typeof dateObj === 'string') return dateObj;

      if (dateObj.startedAt && dateObj.endedAt) {
        // Parse as UTC and extract just the date part
        const startDateStr = dateObj.startedAt.split('T')[0]; // "2025-10-04"
        const endDateStr = dateObj.endedAt.split('T')[0];     // "2025-10-04"

        const startDate = new Date(startDateStr + 'T12:00:00Z'); // Noon UTC to avoid timezone shift
        const endDate = new Date(endDateStr + 'T12:00:00Z');

        // Format dates in a user-friendly way (May 10 - May 14, 2025)
        return `${startDate.toLocaleDateString('en-US', {
          month: 'short', day: 'numeric', timeZone: 'UTC'
        })} - ${endDate.toLocaleDateString('en-US', {
          month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC'
        })}`;
      }
    } catch (e) {
      console.error('Error formatting date range:', e);
    }

    return 'Date Filter';
  }, []);
  
  // Filter remover



  const removeFilter = useCallback((key: string, value?: any) => {
   
    
    // Get fresh state directly from Redux store
    const currentState = store.getState().rootReducers;
    const currentFilters = currentState.leads.filterParam;
    
    // Create a DEEP copy to ensure no mutations affect original
    const newFilter = JSON.parse(JSON.stringify(currentFilters));
    
    // Handle array filters (label, status, teams, etc.)
    if (Array.isArray(newFilter[key]) && value !== undefined) {
      // Remove the specific value from the array
      newFilter[key] = newFilter[key].filter((v: any) => v !== value);
      
      // If the array is now empty, set the property to undefined explicitly
      if (newFilter[key].length === 0) {
        newFilter[key] = undefined; // This is key - use undefined instead of delete
      }
    } else {
      // For non-array properties, set to undefined explicitly
      newFilter[key] = undefined; // Use undefined instead of delete
    }
    
    // Make sure to preserve pagination
    newFilter.paginationParams = { 
      page: 1, 
      perPage: currentFilters.paginationParams?.perPage || 50 
    };
    
    // Dispatch the immutably updated filter
    dispatch(setLeadFilter(newFilter));
    
  }, [dispatch]);
  
  // Clear all filters

const clearAllFilters = useCallback(() => {

  // Get current list ID from Redux
  const currentState = store.getState().rootReducers;
  const currentListId = currentState?.leadName?.leadName?.id;

  logger.log('Current list ID before reset:', currentListId);

  // Reset all filters
  dispatch(resetLeadFilter());

  // Immediately reapply the list ID if it exists and isn't default
  if (currentListId && currentListId !== "0") {
    logger.log('Reapplying list ID after reset:', currentListId);
    dispatch(setLeadFilter({
      list: currentListId,
      paginationParams: { page: 1, perPage: 50 }
    }));
  }

  // ✅ FIX: Clear all column filters
  if (columnFilters && typeof columnFilters === 'object' && onRemoveColumnFilter) {
    logger.log('Clearing column filters:', Object.keys(columnFilters));
    Object.keys(columnFilters).forEach(colId => {
      onRemoveColumnFilter(colId);
    });
  }

  logger.log('Filter reset complete - including column filters');

}, [dispatch, columnFilters, onRemoveColumnFilter]);
  
// Map filter keys to their corresponding sections in the filter modal
  const getFilterSection = useCallback((key: string): string => {
    const mapping: {[key: string]: string} = {
      date: "basic",
      label: "basic",
      status: "basic",
      source: "basic",
      list: "basic",
      teamMembers: "team",
      teams: "team",
      assign: "team",
      taskFilter: "task",
      customFieldFilter: "task",
      activityFilter: "activity",
      isDuplicate: "basic",
      isUntouched: "basic",
      isNotCalled: "basic",
      isNotAssign: "basic",
      isFollowUp: "basic",
      isStaled: "basic",
      products: "products"
    };
    return mapping[key] || "basic";
  }, []);
  
  // Skip rendering if no filters active or during search
  const hasColumnFiltersActive = columnFilters && typeof columnFilters === 'object' && Object.keys(columnFilters).length > 0;
  if ((!hasActiveFilters(filterParam) && !hasColumnFiltersActive) || filterParam.search) {
    return null;
  }
  
  // Render the component
  return (
    <FilterBarContainer data-testid="filter-status-bar">
      <FilterContent>
        <FilterLabel>Active Filters:</FilterLabel>
        
        {/* Boolean filters */}
        {renderBooleanFilters(filterParam, onEditFilter, removeFilter)}

        {/* Quick/Saved Filter */}
        {filterParam.quickFilter && (
          <FilterChip
            color="blue"
            onClick={() => onEditFilter('basic')}
          >
            <Bookmark size={14} />
            <span>Saved: {getQuickFilterName(filterParam.quickFilter)}</span>
            <RemoveButton
              onClick={(e: React.MouseEvent) => {
                e.stopPropagation();
                removeFilter('quickFilter');
              }}
            >
              <X size={14} />
            </RemoveButton>
          </FilterChip>
        )}

        {/* Date filter - ONLY show if not from a column filter (e.g., createdAt column) */}
        {filterParam.date && !columnFilters?.['createdAt'] && (
          <FilterChip
            color="blue"
            onClick={() => onEditFilter(getFilterSection('date'))}
          >
            <Calendar size={14} />
            <span>{formatDateRange(filterParam.date)}</span>
            <RemoveButton
              onClick={(e: React.MouseEvent) => {
                e.stopPropagation();
                removeFilter('date');
              }}
            >
              <X size={14} />
            </RemoveButton>
          </FilterChip>
        )}
        
        {/* Label filters - ONLY show if not from a column filter */}
        {filterParam.label && !columnFilters?.['label'] && filterParam.label.map((labelValue: any) => {
          // ✅ Extract string value (could be string or object)
          const labelId = typeof labelValue === 'string' ? labelValue : (labelValue?.value || labelValue?.label || String(labelValue));
          const labelKey = typeof labelValue === 'string' ? labelValue : (labelValue?.value || JSON.stringify(labelValue));

          return (
            <FilterChip
              key={`label-${labelKey}`}
              color="green"
              onClick={() => onEditFilter(getFilterSection('label'))}
            >
              <Tag size={14} />
              <span>Label: {getLabelName(labelId)}</span>
              <RemoveButton
                onClick={(e: React.MouseEvent) => {
                  e.stopPropagation();
                  removeFilter('label', labelValue);
                }}
              >
                <X size={14} />
              </RemoveButton>
            </FilterChip>
          );
        })}

        {/* Status filters - ONLY show if not from a column filter */}
        {filterParam.status && !columnFilters?.['status'] && filterParam.status.map((statusValue: any) => {
          // ✅ Extract string value (could be string or object)
          const statusId = typeof statusValue === 'string' ? statusValue : (statusValue?.value || statusValue?.label || String(statusValue));
          const statusKey = typeof statusValue === 'string' ? statusValue : (statusValue?.value || JSON.stringify(statusValue));

          return (
            <FilterChip
              key={`status-${statusKey}`}
              color="purple"
              onClick={() => onEditFilter(getFilterSection('status'))}
            >
              <Filter size={14} />
              <span>Status: {getStatusName(statusId)}</span>
              <RemoveButton
                onClick={(e: React.MouseEvent) => {
                  e.stopPropagation();
                  removeFilter('status', statusValue);
                }}
              >
                <X size={14} />
              </RemoveButton>
            </FilterChip>
          );
        })}
        
        {/* Source filters - ONLY show if not from a column filter (e.g., integration column) */}
        {filterParam.source && !columnFilters?.['integration'] && filterParam.source.map((sourceValue: any) => {
          // ✅ Extract string value (could be string or object)
          const sourceId = typeof sourceValue === 'string' ? sourceValue : (sourceValue?.value || sourceValue?.label || String(sourceValue));
          const sourceKey = typeof sourceValue === 'string' ? sourceValue : (sourceValue?.value || JSON.stringify(sourceValue));

          return (
            <FilterChip
              key={`source-${sourceKey}`}
              color="orange"
              onClick={() => onEditFilter(getFilterSection('source'))}
            >
              <ArrowDownUp size={14} />
              <span>Source: {getSourceName(sourceId)}</span>
              <RemoveButton
                onClick={(e: React.MouseEvent) => {
                  e.stopPropagation();
                  removeFilter('source', sourceValue);
                }}
              >
                <X size={14} />
              </RemoveButton>
            </FilterChip>
          );
        })}
        
        {/* Team members filters - ONLY show when isAssign is NOT set */}
        {filterParam.teamMembers && !filterParam.isAssign && filterParam.teamMembers.map((memberId: any) => {
          // ✅ Extract string value (could be string or object)
          const memberIdStr = typeof memberId === 'string' ? memberId : (memberId?.value || memberId?.label || String(memberId));
          const memberKey = typeof memberId === 'string' ? memberId : (memberId?.value || JSON.stringify(memberId));

          return (
            <FilterChip
              key={`member-${memberKey}`}
              color="indigo"
              onClick={() => onEditFilter(getFilterSection('teamMembers'))}
            >
              <User size={14} />
              <span>User: {getTeamMemberName(memberIdStr)}</span>
              <RemoveButton
                onClick={(e: React.MouseEvent) => {
                  e.stopPropagation();
                  removeFilter('teamMembers', memberId);
                }}
              >
                <X size={14} />
              </RemoveButton>
            </FilterChip>
          );
        })}

        {/* Assigned to filters - ONLY show when isAssign IS set */}
        {filterParam.isAssign && filterParam.teamMembers && filterParam.teamMembers.map((memberId: any) => {
          // ✅ Extract string value (could be string or object)
          const memberIdStr = typeof memberId === 'string' ? memberId : (memberId?.value || memberId?.label || String(memberId));
          const memberKey = typeof memberId === 'string' ? memberId : (memberId?.value || JSON.stringify(memberId));

          return (
            <FilterChip
              key={`assigned-${memberKey}`}
              color="pink"
              onClick={() => onEditFilter(getFilterSection('assign'))}
            >
              <User size={14} />
              <span>Assigned to: {getTeamMemberName(memberIdStr)}</span>
              <RemoveButton
                onClick={(e: React.MouseEvent) => {
                  e.stopPropagation();
                  // Remove specific member from teamMembers array
                  const currentState = store.getState().rootReducers;
                  const currentFilters = currentState.leads.filterParam;
                  const newFilter = JSON.parse(JSON.stringify(currentFilters));

                  if (newFilter.teamMembers) {
                    newFilter.teamMembers = newFilter.teamMembers.filter((id: any) => {
                      const idStr = typeof id === 'string' ? id : (id?.value || id?.label || String(id));
                      return idStr !== memberIdStr;
                    });

                    // If no more members, remove both isAssign and teamMembers
                    if (newFilter.teamMembers.length === 0) {
                      newFilter.isAssign = undefined;
                      newFilter.teamMembers = undefined;
                    }
                  }

                  newFilter.paginationParams = {
                    page: 1,
                    perPage: currentFilters.paginationParams?.perPage || 50
                  };

                  dispatch(setLeadFilter(newFilter));
                }}
              >
                <X size={14} />
              </RemoveButton>
            </FilterChip>
          );
        })}
        
        {/* Teams filter */}
        {filterParam.teams && filterParam.teams.map((teamId: any) => {
          // ✅ Extract string value (could be string or object)
          const teamIdStr = typeof teamId === 'string' ? teamId : (teamId?.value || teamId?.label || String(teamId));
          const teamKey = typeof teamId === 'string' ? teamId : (teamId?.value || JSON.stringify(teamId));

          return (
            <FilterChip
              key={`team-${teamKey}`}
              color="violet"
              onClick={() => onEditFilter(getFilterSection('teams'))}
            >
              <User size={14} />
              <span>Team: {getTeamName(teamIdStr)}</span>
              <RemoveButton
                onClick={(e: React.MouseEvent) => {
                  e.stopPropagation();
                  removeFilter('teams', teamId);
                }}
              >
                <X size={14} />
              </RemoveButton>
            </FilterChip>
          );
        })}
        
        {/* Organization filter */}
{filterParam.byOrganization && (
  <FilterChip 
    color="indigo"
    onClick={() => onEditFilter('team')}
  >
    <User size={14} />
    <span>Organization</span>
    <RemoveButton
      onClick={(e: React.MouseEvent) => {
        e.stopPropagation();
        removeFilter('byOrganization');
      }}
    >
      <X size={14} />
    </RemoveButton>
  </FilterChip>
)}

        {/* Activity Filters - Each component of activity filter */}
        {filterParam.activityFilter && (
          <>
            {/* Activity Types */}
            {filterParam.activityFilter.types && filterParam.activityFilter.types.map((type: any) => {
              // ✅ Extract string value from type (could be string or object)
              const typeValue = typeof type === 'string' ? type : (type?.value || type?.label || String(type));
              const typeKey = typeof type === 'string' ? type : (type?.value || type?.label || JSON.stringify(type));

              return (
                <FilterChip
                  key={`activity-type-${typeKey}`}
                  color="cyan"
                  onClick={() => onEditFilter('activity')}
                >
                  <MessageCircle size={14} />
                  <span>Activity: {typeValue}</span>
                  <RemoveButton onClick={(e: React.MouseEvent) => {
                    e.stopPropagation();
                    // Create new array without this type (compare by value for objects)
                    const newTypes = filterParam.activityFilter?.types?.filter((t: any) => {
                      const tValue = typeof t === 'string' ? t : (t?.value || t?.label || String(t));
                      return tValue !== typeValue;
                    }) || [];

                    // Update filter with modified types array
                    const newFilter = {...filterParam};
                    if (newTypes.length > 0) {
                      if (!newFilter.activityFilter) newFilter.activityFilter = {};
                      newFilter.activityFilter.types = newTypes;
                    } else if (newFilter.activityFilter) {
                      delete newFilter.activityFilter.types;
                      // If activityFilter is now empty, delete it
                      if (Object.keys(newFilter.activityFilter).length === 0) {
                        delete newFilter.activityFilter;
                      }
                    }

                    dispatch(setLeadFilter(newFilter));
                  }}>
                    <X size={14} />
                  </RemoveButton>
                </FilterChip>
              );
            })}
            
            {/* Call Duration */}
            {filterParam.activityFilter.duration !== undefined && (
              <FilterChip 
                color="cyan"
                onClick={() => onEditFilter('activity')}
              >
                <Clock size={14} />
                <span>
                  Call Duration {formatOperator(filterParam.activityFilter.durationOperator || 'eq')} {filterParam.activityFilter.duration}s
                </span>
                <RemoveButton onClick={(e: React.MouseEvent) => {
                  e.stopPropagation();
                  const newFilter = {...filterParam};
                  if (newFilter.activityFilter) {
                    delete newFilter.activityFilter.duration;
                    delete newFilter.activityFilter.durationOperator;
                    
                    // If activityFilter is now empty, delete it
                    if (Object.keys(newFilter.activityFilter).length === 0) {
                      delete newFilter.activityFilter;
                    }
                  }
                  dispatch(setLeadFilter(newFilter));
                }}>
                  <X size={14} />
                </RemoveButton>
              </FilterChip>
            )}
            
            {/* Call Count */}
            {filterParam.activityFilter.callCount !== undefined && (
              <FilterChip 
                color="cyan"
                onClick={() => onEditFilter('activity')}
              >
                <Phone size={14} />
                <span>
                  Call Count {formatOperator(filterParam.activityFilter.callCountOperator || 'eq')} {filterParam.activityFilter.callCount}
                </span>
                <RemoveButton onClick={(e: React.MouseEvent) => {
                  e.stopPropagation();
                  const newFilter = {...filterParam};
                  if (newFilter.activityFilter) {
                    delete newFilter.activityFilter.callCount;
                    delete newFilter.activityFilter.callCountOperator;
                    
                    // If activityFilter is now empty, delete it
                    if (Object.keys(newFilter.activityFilter).length === 0) {
                      delete newFilter.activityFilter;
                    }
                  }
                  dispatch(setLeadFilter(newFilter));
                }}>
                  <X size={14} />
                </RemoveButton>
              </FilterChip>
            )}
            
            {/* Activity Date Range */}
            {filterParam.activityFilter.range && (
              <FilterChip 
                color="cyan"
                onClick={() => onEditFilter('activity')}
              >
                <Calendar size={14} />
                <span>
                  Activity Date: {formatDateRange({
                    startedAt: filterParam.activityFilter.range.start,
                    endedAt: filterParam.activityFilter.range.end
                  })}
                </span>
                <RemoveButton onClick={(e: React.MouseEvent) => {
                  e.stopPropagation();
                  const newFilter = {...filterParam};
                  if (newFilter.activityFilter) {
                    delete newFilter.activityFilter.range;
                    
                    // If activityFilter is now empty, delete it
                    if (Object.keys(newFilter.activityFilter).length === 0) {
                      delete newFilter.activityFilter;
                    }
                  }
                  dispatch(setLeadFilter(newFilter));
                }}>
                  <X size={14} />
                </RemoveButton>
              </FilterChip>
            )}
          </>
        )}
        
        {/* Task Filter */}
        {filterParam.taskFilter && (
          <>
            {/* Task Types */}
            {filterParam.taskFilter.types && filterParam.taskFilter.types.map((type: any) => {
              // ✅ Extract string value from type (could be string or object)
              const typeValue = typeof type === 'string' ? type : (type?.value || type?.label || String(type));
              const typeKey = typeof type === 'string' ? type : (type?.value || type?.label || JSON.stringify(type));

              return (
                <FilterChip
                  key={`task-type-${typeKey}`}
                  color="amber"
                  onClick={() => onEditFilter('task')}
                >
                  <BellRing size={14} />
                  <span>Task: {typeValue}</span>
                  <RemoveButton onClick={(e: React.MouseEvent) => {
                    e.stopPropagation();
                    // Create new array without this type (compare by value for objects)
                    const newTypes = filterParam.taskFilter?.types?.filter((t: any) => {
                      const tValue = typeof t === 'string' ? t : (t?.value || t?.label || String(t));
                      return tValue !== typeValue;
                    }) || [];

                    // Update filter with modified types array
                    const newFilter = {...filterParam};
                    if (newTypes.length > 0) {
                      if (!newFilter.taskFilter) newFilter.taskFilter = {};
                      newFilter.taskFilter.types = newTypes;
                    } else if (newFilter.taskFilter) {
                      delete newFilter.taskFilter.types;
                      // If taskFilter is now empty, delete it
                      if (Object.keys(newFilter.taskFilter).length === 0) {
                        delete newFilter.taskFilter;
                      }
                    }

                    dispatch(setLeadFilter(newFilter));
                  }}>
                    <X size={14} />
                  </RemoveButton>
                </FilterChip>
              );
            })}
            
            {/* Task Date Range */}
            {filterParam.taskFilter.range && (
              <FilterChip 
                color="amber"
                onClick={() => onEditFilter('task')}
              >
                <Calendar size={14} />
                <span>
                  Task Date: {formatDateRange({
                    startedAt: filterParam.taskFilter.range.start,
                    endedAt: filterParam.taskFilter.range.end
                  })}
                </span>
                <RemoveButton onClick={(e: React.MouseEvent) => {
                  e.stopPropagation();
                  const newFilter = {...filterParam};
                  if (newFilter.taskFilter) {
                    delete newFilter.taskFilter.range;
                    
                    // If taskFilter is now empty, delete it
                    if (Object.keys(newFilter.taskFilter).length === 0) {
                      delete newFilter.taskFilter;
                    }
                  }
                  dispatch(setLeadFilter(newFilter));
                }}>
                  <X size={14} />
                </RemoveButton>
              </FilterChip>
            )}
          </>
        )}
        
        {/* Custom Field Filter - ONLY show if no column filters are active (to avoid duplicates) */}
        {filterParam.customFieldFilter && !hasColumnFiltersActive && (() => {
          // ✅ Extract display value safely (handle objects, arrays, primitives)
          const getDisplayValue = (value: any): string => {
            if (typeof value === 'string' || typeof value === 'number') {
              return String(value);
            } else if (Array.isArray(value)) {
              // For arrays (multi-selection), extract labels
              return value.map((v: any) =>
                typeof v === 'object' ? (v.label || v.value || v) : v
              ).join(', ');
            } else if (typeof value === 'object' && value !== null) {
              // For objects (selection), extract label or value
              return value.label || value.value || String(value);
            }
            return String(value);
          };

          return (
            <FilterChip
              color="teal"
              onClick={() => onEditFilter('task')}
            >
              <Database size={14} />
              <span>
                {filterParam.customFieldFilter.fieldName}
                {filterParam.customFieldFilter.single &&
                  ` ${formatOperator(filterParam.customFieldFilter.single.operator)} ${getDisplayValue(filterParam.customFieldFilter.single.value)}`}
                {filterParam.customFieldFilter.range &&
                  ` between ${getDisplayValue(filterParam.customFieldFilter.range.start.value)} and ${getDisplayValue(filterParam.customFieldFilter.range.end.value)}`}
              </span>
              <RemoveButton onClick={(e: React.MouseEvent) => {
                e.stopPropagation();
                removeFilter('customFieldFilter');
              }}>
                <X size={14} />
              </RemoveButton>
            </FilterChip>
          );
        })()}

     {/* Products Filter */}
        {filterParam.products && filterParam.products.map((productId: any) => {
          // ✅ Extract string value (could be string or object)
          const productIdStr = typeof productId === 'string' ? productId : (productId?.value || productId?.label || String(productId));
          const productKey = typeof productId === 'string' ? productId : (productId?.value || JSON.stringify(productId));

          return (
            <FilterChip
              key={`product-${productKey}`}
              color="emerald"
              onClick={() => onEditFilter('products')}
            >
              <Package size={14} />
              <span>Product: {getProductName(productIdStr)}</span>
              <RemoveButton
                onClick={(e: React.MouseEvent) => {
                  e.stopPropagation();
                  removeFilter('products', productId);
                }}
              >
                <X size={14} />
              </RemoveButton>
            </FilterChip>
          );
        })}

        {/* Column Filters */}
        {columnFilters && typeof columnFilters === 'object' && !Array.isArray(columnFilters) && Object.entries(columnFilters).map(([colId, filterConfig]: [string, any]) => {
          // Defensive checks for filter config
          if (!filterConfig || typeof filterConfig !== 'object' || Array.isArray(filterConfig) ||
              !filterConfig.operator || filterConfig.value === undefined || filterConfig.value === null) {
            return null;
          }

          try {
            const displayName = getColumnDisplayName(colId);
            const operator = formatOperator(filterConfig.operator);

            // ✅ Handle various value types safely - MUST convert to string!
            let valueStr = '';
            if (typeof filterConfig.value === 'string' || typeof filterConfig.value === 'number') {
              valueStr = String(filterConfig.value);
            } else if (Array.isArray(filterConfig.value)) {
              // For array values, extract and join - ENSURE STRING CONVERSION
              valueStr = filterConfig.value.map((v: any) => {
                if (typeof v === 'string' || typeof v === 'number') {
                  return String(v);
                } else if (typeof v === 'object' && v !== null) {
                  // Extract label or value and convert to string
                  return String(v.label || v.value || v);
                }
                return String(v);
              }).join(', ');
            } else if (typeof filterConfig.value === 'object' && filterConfig.value !== null) {
              // For object values - ENSURE STRING CONVERSION
              if (filterConfig.value.label) {
                valueStr = String(filterConfig.value.label);
              } else if (filterConfig.value.value) {
                valueStr = String(filterConfig.value.value);
              } else {
                valueStr = String(filterConfig.value);
              }
            } else {
              // Fallback: convert to string carefully
              valueStr = String(filterConfig.value || '');
            }

            // Remove any [object Object] remnants
            valueStr = valueStr.replace(/\[object Object\]/g, '').trim() || 'N/A';

            const value = valueStr.length > 15
              ? valueStr.substring(0, 12) + '...'
              : valueStr;

            return (
              <ColumnFilterChip
                key={`column-${colId}`}
                onClick={() => onEditFilter('basic')}
                title={`${displayName} ${operator} ${valueStr.substring(0, 50)}`}
              >
                <Sliders size={13} />
                <ColumnFilterContent>
                  <ColumnName>{displayName}</ColumnName>
                  <OperatorValue>
                    <span className="operator">{operator}</span>
                    <span className="value">{value}</span>
                  </OperatorValue>
                </ColumnFilterContent>
                <RemoveButton
                  onClick={(e: React.MouseEvent) => {
                    e.stopPropagation();
                    onRemoveColumnFilter?.(colId);
                  }}
                >
                  <X size={13} />
                </RemoveButton>
              </ColumnFilterChip>
            );
          } catch (error) {
            logger.error('Error rendering column filter:', error);
            return null;
          }
        })}


        {/* Clear all button */}
        <ClearButton onClick={clearAllFilters}>
          <X size={14} />
          Clear all
        </ClearButton>
      </FilterContent>
    </FilterBarContainer>
  );
};

// Helper function to check if we have any active filters
function hasActiveFilters(filterParam: FilterParams): boolean {
  if (!filterParam) return false;
  
   // Explicitly check for byOrganization
   if (filterParam.byOrganization === true) {
    return true;
  }
  

  // These keys shouldn't trigger the filter bar to show
  const ignoredKeys = ['paginationParams', 'sort', 'list', 'search'];
  
  return Object.keys(filterParam).some(key => {
    // Skip ignored keys
    if (ignoredKeys.includes(key)) return false;
    
    const value = filterParam[key];
    
    // Check if arrays have values
    if (Array.isArray(value)) return value.length > 0;
    
    // For boolean flags, just check if they're true
    if (typeof value === 'boolean') return value === true;
    
    // For objects (like nested filters), check if they exist and have properties
    if (typeof value === 'object' && value !== null) {
      return Object.keys(value).length > 0;
    }
    
    // For other values, check if defined
    return value !== undefined && value !== null;
  });
}

// Render boolean filters
function renderBooleanFilters(
  filterParam: FilterParams, 
  onEditFilter: (section: string) => void, 
  removeFilter: (key: string) => void
) {
  const booleanFilters = [
    { key: 'isUntouched', label: 'Untouched', section: 'basic' },
    { key: 'isNotCalled', label: 'Not Called', section: 'basic' },
    { key: 'isNotAssign', label: 'Not Assigned', section: 'basic' },
    { key: 'isFollowUp', label: 'No Follow Up', section: 'basic' },
    { key: 'isStaled', label: 'Staled', section: 'basic' },
    { key: 'isDuplicate', label: 'Duplicates', section: 'basic' }
  ];
  
  return booleanFilters.map(filter => 
    filterParam[filter.key] ? (
      <FilterChip 
        key={filter.key}
        color="gray"
        onClick={() => onEditFilter(filter.section)}
      >
        <Filter size={14} />
        <span>{filter.label}</span>
        <RemoveButton
          onClick={(e: React.MouseEvent) => {
            e.stopPropagation();
            removeFilter(filter.key);
          }}
        >
          <X size={14} />
        </RemoveButton>
      </FilterChip>
    ) : null
  );
}

// Styled components - FIXED: Prevent filter bar from pushing grid down
const FilterBarContainer = styled.div`
  background-color: #f8fafc;
  border-bottom: 1px solid #e2e8f0;
  padding: 8px 12px;
  margin-bottom: 8px;
  max-height: 60px; /* ✅ Fixed maximum height */
  overflow-y: auto; /* ✅ Scroll if content exceeds height */
  overflow-x: hidden;
  position: relative;

  /* ✅ Subtle gradient fade at bottom to indicate scrollable content */
  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 12px;
    background: linear-gradient(to bottom, transparent, #f8fafc);
    pointer-events: none;
    opacity: 0;
    transition: opacity 0.2s;
  }

  /* Show gradient when scrolled */
  &:not(:hover)::after {
    opacity: 0;
  }

  /* Custom scrollbar styling */
  &::-webkit-scrollbar {
    width: 6px;
    height: 6px;
  }

  &::-webkit-scrollbar-track {
    background: #f1f5f9;
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb {
    background: #cbd5e1;
    border-radius: 3px;

    &:hover {
      background: #94a3b8;
    }
  }
`;

const FilterContent = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  min-height: 32px; /* ✅ Minimum height to prevent collapse */
`;

const FilterLabel = styled.span`
  color: #64748b;
  font-size: 13px;
  font-weight: 500;
  margin-right: 8px;
`;

const FilterChip = styled.div<{ color: string }>`
  display: flex;
  align-items: center;
  background: white;
  border-radius: 999px;
  padding: 4px 10px;
  font-size: 13px;
  gap: 6px;
  cursor: pointer;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
  transition: all 0.2s;
  max-width: 300px;
  overflow: hidden;
  
  /* Text ellipsis for long content */
  span {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  
  ${({ color }) => {
    switch (color) {
      case 'blue':
        return `
          border: 1px solid #bfdbfe;
          color: #1e40af;
          &:hover { background-color: #eff6ff; }
          svg { color: #3b82f6; }
        `;
      case 'green':
        return `
          border: 1px solid #bbf7d0;
          color: #166534;
          &:hover { background-color: #f0fdf4; }
          svg { color: #22c55e; }
        `;
      case 'purple':
        return `
          border: 1px solid #ddd6fe;
          color: #5b21b6;
          &:hover { background-color: #f5f3ff; }
          svg { color: #8b5cf6; }
        `;
      case 'orange':
        return `
          border: 1px solid #fed7aa;
          color: #9a3412;
          &:hover { background-color: #fff7ed; }
          svg { color: #f97316; }
        `;
      case 'indigo':
        return `
          border: 1px solid #c7d2fe;
          color: #3730a3;
          &:hover { background-color: #eef2ff; }
          svg { color: #6366f1; }
        `;
      case 'cyan':
        return `
          border: 1px solid #a5f3fc;
          color: #155e75;
          &:hover { background-color: #ecfeff; }
          svg { color: #06b6d4; }
        `;
      case 'amber':
        return `
          border: 1px solid #fde68a;
          color: #92400e;
          &:hover { background-color: #fffbeb; }
          svg { color: #f59e0b; }
        `;
      case 'teal':
        return `
          border: 1px solid #99f6e4;
          color: #115e59;
          &:hover { background-color: #f0fdfa; }
          svg { color: #14b8a6; }
        `;
      case 'violet':
        return `
          border: 1px solid #ddd6fe;
          color: #5b21b6;
          &:hover { background-color: #f5f3ff; }
          svg { color: #8b5cf6; }
        `;
        case 'emerald':
        return `
          border: 1px solid #a7f3d0;
          color: #065f46;
          &:hover { background-color: #ecfdf5; }
          svg { color: #10b981; }
        `;
      case 'pink':
        return `
          border: 1px solid #fbcfe8;
          color: #831843;
          &:hover { background-color: #fdf2f8; }
          svg { color: #ec4899; }
        `;
      default:
        return `
          border: 1px solid #e2e8f0;
          color: #475569;
          &:hover { background-color: #f8fafc; }
          svg { color: #64748b; }
        `;
    }
  }}
`;

const RemoveButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  background: none;
  color: #94a3b8;
  cursor: pointer;
  padding: 0;
  min-width: 16px;
  transition: color 0.2s;
  flex-shrink: 0;
  
  &:hover {
    color: #ef4444;
  }
`;

const ClearButton = styled.button`
  display: flex;
  align-items: center;
  gap: 4px;
  margin-left: auto;
  background: none;
  border: none;
  color: #ef4444;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  padding: 4px 8px;
  border-radius: 4px;

  &:hover {
    background-color: #fef2f2;
  }
`;

// Enhanced styled component for column filters
const ColumnFilterChip = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  background: linear-gradient(135deg, #faf5ff 0%, #fdf2f8 100%);
  border: 1.5px solid #e9d5ff;
  border-radius: 8px;
  padding: 6px 12px;
  font-size: 12px;
  cursor: pointer;
  transition: all 0.2s ease;
  max-width: 320px;
  flex-shrink: 0;

  &:hover {
    background: linear-gradient(135deg, #f3e8ff 0%, #fce7f3 100%);
    border-color: #ddd6fe;
    box-shadow: 0 2px 8px rgba(147, 51, 234, 0.15);
  }

  svg {
    color: #a78bfa;
    flex-shrink: 0;
  }
`;

const ColumnFilterContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`;

const ColumnName = styled.span`
  font-weight: 600;
  color: #5b21b6;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 11px;
`;

const OperatorValue = styled.div`
  display: flex;
  gap: 6px;
  align-items: center;
  font-size: 11px;
  color: #7c3aed;

  .operator {
    font-weight: 700;
    color: #6d28d9;
    min-width: fit-content;
  }

  .value {
    color: #6d28d9;
    font-family: 'Monaco', 'Courier New', monospace;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    max-width: 200px;
  }
`;

export default FilterStatusBar;